import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { toast } from "../../../../utils/appToast";
import Cookies from "js-cookie";
import Checkbox from "../../../../Checkbox";
import Pagination from "../../../../components/Pagination/Pagination";
import { Search } from "lucide-react";
import api from "../../../chat/api";
import RenameModal from "./RenameModal";
import DeleteModal from "./DeleteModal";
import TaskActionMenu from "./TaskActionMenu";
import DateCalendar from "./DateCalendar";
import TabLayout from "./TabLayout";
import ViewFileModal from "./ViewFileModal";
import {
  SpinnerIcon,
  CheckIcon,
  ClockIcon,
  ErrorIcon,
  DocumentIcon,
  SortIcon,
  ThreeDotsIcon,
} from "../../KnowledgeBase/Icons";
import ActionMenu from "./ActionMenu";
import DynamicTags from "./DynamicTags";
import ProjectTagModal from "./ProjectTagModal";
import ViewAndIngestModal from "./ViewAndIngestModal";
import FilterDropdown from "./FilterDropdown";
import SummaryPopover from "./SummaryPopover";
import {
  consumeUploadError,
  getUploadState,
  releaseUploads,
  startUpload,
  subscribeUploads,
} from "./uploadRegistry";

const getInitials = (name) => {
  if (!name || name === "N/A") return "?";
  if (name.toLowerCase() === "you") return "Y";
  
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const ProjectFilesTab = () => {
  const {
    files, loading, fetchFiles, setTabUploadHandler, setIsTabBusy,
    tenderImport, retryTenderImport, retryingImport, importStalled,
  } = useOutletContext();

  // Documents the tender import could not download. Only "failed" is offered a
  // retry: restricted files, external links and unsupported types are facts
  // about the file that no retry changes.
  const failedImportFiles = (tenderImport?.files || []).filter((f) => f.status === "failed");
  const importIsRunning = ["pending", "running"].includes(tenderImport?.status);

  const params = useParams();
  const folderId = params.projectId || params.id;

  // Both seeded from the registry synchronously, so a remount mid-upload paints
  // the "uploading" rows on the first frame rather than flashing a skeleton and
  // filling in afterwards.
  const [localFiles, setLocalFiles] = useState(() => getUploadState(folderId).temps);
  // Uploads in flight are held outside this component, because switching tabs
  // unmounts it and used to take the "uploading" rows with it. See
  // uploadRegistry.js.
  const [pendingUploads, setPendingUploads] = useState(() => getUploadState(folderId));
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const [activePollingDocs, setActivePollingDocs] = useState(new Set());
  const activePollingDocsRef = useRef(activePollingDocs);
  const isPollingRef = useRef(false);
  const pendingIngestRef = useRef(new Set());
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const [activeSummaryId, setActiveSummaryId] = useState(null);
  const [summaryAnchorEl, setSummaryAnchorEl] = useState(null);
  const summaryModalRef = useRef(null);
  
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(null);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);

  const [controlsPortalNode, setControlsPortalNode] = useState(null);
  const [footerPortalNode, setFooterPortalNode] = useState(null);

  const [currentFile, setCurrentFile] = useState(null);
  const [isRenamingDoc, setIsRenamingDoc] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameInput, setRenameInput] = useState("");
  
  const [fileToView, setFileToView] = useState(null);
  const [isViewFileModalOpen, setIsViewFileModalOpen] = useState(false);

  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [tagModalDocIds, setTagModalDocIds] = useState([]);
  // Doc ids whose row shows the "Applying…" pill while the table refetches after a tag change.
  const [applyingTagDocIds, setApplyingTagDocIds] = useState(() => new Set());

  const [viewAndIngestItem, setViewAndIngestItem] = useState(null);

  const [activeFilters, setActiveFilters] = useState({ fileTypes: [], statuses: [], tags: [] });
  const [tagsRefreshTrigger, setTagsRefreshTrigger] = useState(0);

  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const savedWidths = localStorage.getItem("projectFiles_columnWidths");
      return savedWidths ? JSON.parse(savedWidths) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    if (setTabUploadHandler) {
      setTabUploadHandler(() => () => {
        const input = document.getElementById("project-hidden-file-input");
        if (input) input.click();
      });
    }
    return () => {
      if (setTabUploadHandler) setTabUploadHandler(null);
    };
  }, [setTabUploadHandler]);

  useEffect(() => {
    setControlsPortalNode(document.getElementById("project-tab-controls-portal"));
    setFooterPortalNode(document.getElementById("project-tab-footer-portal"));
  }, []);

  useEffect(() => {
    activePollingDocsRef.current = activePollingDocs;
  }, [activePollingDocs]);

  // Follow this folder's in-flight uploads. Keyed on folderId so two projects
  // cannot show each other's rows.
  useEffect(() => {
    setPendingUploads(getUploadState(folderId));
    return subscribeUploads(folderId, setPendingUploads);
  }, [folderId]);

  // Opening a different project must not inherit the previous one's rows. This
  // route keeps the same component when :projectId changes, so carried-over
  // optimistic rows would otherwise show under the wrong project until its own
  // files arrived.
  const shownFolderRef = useRef(folderId);
  useEffect(() => {
    if (shownFolderRef.current === folderId) return;
    shownFolderRef.current = folderId;
    setLocalFiles(getUploadState(folderId).temps);
    setActivePollingDocs(new Set());
  }, [folderId]);

  // An upload that failed while this tab was unmounted still needs reporting.
  // consumeUploadError clears it on read, so returning to the tab twice cannot
  // announce the same failure twice.
  useEffect(() => {
    const message = consumeUploadError(folderId);
    if (message) toast.error(message);
  }, [folderId, pendingUploads]);

  // Resume polling for documents uploaded while this tab was away. Without
  // this they sit at "processing" until something else happens to refetch.
  useEffect(() => {
    if (pendingUploads.pollingKeys.length === 0) return;
    setActivePollingDocs((prev) => {
      let changed = false;
      const next = new Set(prev);
      pendingUploads.pollingKeys.forEach((key) => {
        if (!next.has(key)) {
          next.add(key);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [pendingUploads.pollingKeys]);

  // Hand settled documents back, so the registry stops re-seeding rows the
  // server now reports properly and the folder's entry can be released.
  useEffect(() => {
    const settled = localFiles
      .filter((doc) => {
        const st = (doc.status || "").toLowerCase();
        return st.includes("ready") || st === "ingested" || st === "completed"
          || st === "error" || st === "failed";
      })
      .map((doc) => doc.key || doc.pdf_key || doc.id)
      .filter(Boolean);
    if (settled.length > 0) releaseUploads(folderId, settled);
  }, [localFiles, folderId]);

  // Stop tracking any doc that has already reached a terminal status. The parent
  // ProjectPage runs its own /files/ poll, so a doc can flip to 'ingested' in
  // localFiles before this tab's /status/ poll notices — leaving its key stuck in
  // activePollingDocs (hasChanges is false, so the key is never removed). That kept
  // isTabBusy true and the header's Upload button disabled even after ingestion.
  useEffect(() => {
    setActivePollingDocs((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set(prev);
      localFiles.forEach((doc) => {
        const key = doc.key || doc.pdf_key || doc.id;
        const st = (doc.status || "").toLowerCase();
        const terminal =
          st.includes("ready") || st === "ingested" || st === "completed" || st === "error" || st === "failed";
        if (key && terminal && next.has(key)) {
          next.delete(key);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [localFiles]);

  // Report upload/ingest activity up to the header so it can disable the Upload button.
  useEffect(() => {
    if (setIsTabBusy) {
      setIsTabBusy(isUploadingFiles || pendingUploads.uploading || activePollingDocs.size > 0);
    }
    return () => { if (setIsTabBusy) setIsTabBusy(false); };
  }, [isUploadingFiles, pendingUploads.uploading, activePollingDocs, setIsTabBusy]);

  useEffect(() => {
    if (files) {
      setLocalFiles((prev) => {
        const backendKeys = new Set(files.map((f) => f.key || f.pdf_key || f.id));

        // Placeholders come from the registry alone, never from prev. Nothing
        // here edits them in place, and the registry is what knows when an
        // upload has finished — keeping a copy from prev would strand the
        // "uploading" row on screen forever once the real one arrived.
        const tempDocs = pendingUploads.temps;

        // Just-uploaded rows do come from both: the registry supplies them after
        // a remount, and prev keeps them alive in the window between a document
        // settling and the backend list catching up.
        const ownOptimistic = prev.filter((p) =>
          !p.isTemp && p.isOptimistic && !backendKeys.has(p.key || p.pdf_key || p.id)
        );
        const ownOptimisticKeys = new Set(ownOptimistic.map((p) => p.key || p.pdf_key || p.id));
        const optimisticDocs = [
          ...ownOptimistic,
          ...pendingUploads.optimistic.filter((d) => {
            const key = d.key || d.pdf_key || d.id;
            return !backendKeys.has(key) && !ownOptimisticKeys.has(key);
          }),
        ];
        // While uploads/ingests are in flight a transient/partial /files/ response can
        // omit real rows — keep them instead of dropping them off the table.
        const carriedDocs = activePollingDocsRef.current.size > 0
          ? prev.filter((p) => !p.isTemp && !p.isOptimistic && !p.isTenderImport && !backendKeys.has(p.key || p.pdf_key || p.id))
          : [];
        const mergedFiles = files.map((f) => {
          const fKey = f.key || f.pdf_key || f.id;
          const existing = prev.find(p => (p.key || p.pdf_key || p.id) === fKey);
          const eStatus = (existing?.status || "").toLowerCase();
          const bStatus = (f.status || "").toLowerCase();
          // A just-approved doc may still report the stale 'ready_for_review' verdict
          // while ingestion spins up — keep showing it as ingesting until it progresses.
          if (existing && pendingIngestRef.current.has(fKey) && bStatus === "ready_for_review") {
            return { ...f, status: existing.status };
          }
          // A lagging /files/ verdict can still say 'ready_for_review' after the doc
          // finished ingesting — don't pull it backwards.
          if (existing && bStatus === "ready_for_review" && (eStatus === "ingested" || eStatus === "completed" || eStatus === "ready")) {
            return { ...f, status: existing.status };
          }
          if (existing && existing.isOptimistic) {
             if (bStatus.includes("ready") || bStatus === "ingested" || bStatus === "completed" || bStatus === "error" || bStatus === "failed") {
                return f;
             }
             return { ...f, ...existing };
          }
          return f;
        });
        return [...tempDocs, ...optimisticDocs, ...carriedDocs, ...mergedFiles];
      });
    }
  }, [files, pendingUploads]);

  const handleFileChange = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    if (selectedFiles.length > 5) {
      toast.error("You can only upload a maximum of 5 files at once.");
      event.target.value = null;
      return;
    }
    event.target.value = null;

    setIsUploadingFiles(true);

    try {
      // The request runs in the registry rather than here, so leaving this tab
      // mid-upload neither loses the placeholder rows nor orphans the response.
      // Placeholders, optimistic rows and polling keys all arrive back through
      // the pendingUploads subscription.
      await startUpload(folderId, selectedFiles);

      // Rejections are recorded on the registry rather than raised, so that a
      // duplicate refused while the user was on another tab is still reported
      // when they come back. Whichever of this line or the mount effect reaches
      // it first announces it, and the other finds it already cleared.
      const rejected = consumeUploadError(folderId);
      if (rejected) toast.error(rejected);

      setTimeout(() => {
        pollDocumentStatuses();
      }, 500);
    } catch (error) {
      console.error("Upload error:", error);
      // The registry recorded the failure; consume it here so it is announced
      // once, by whichever of this handler or the mount effect gets there first.
      const message = consumeUploadError(folderId);
      if (message) toast.error(message);
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const localFilesRef = useRef(localFiles);
  useEffect(() => {
    localFilesRef.current = localFiles;
  }, [localFiles]);

  const pollDocumentStatuses = useCallback(async () => {
    if (isPollingRef.current) return; // skip while a poll cycle is already running
    const pendingDocs = localFilesRef.current.filter((doc) => {
      const docKey = doc.key || doc.pdf_key || doc.id;
      return docKey && activePollingDocsRef.current.has(docKey);
    });
    if (pendingDocs.length === 0) return;

    isPollingRef.current = true;
    try {
      const statusPromises = pendingDocs.map(async (doc) => {
        const docKey = doc.key || doc.pdf_key || doc.id;
        try {
          const res = await api.get(`/api/v1/rfp/folders/${folderId}/documents/${docKey}/status/`);
          const type = (res.data?.type || "").toLowerCase();
          const msg = (res.data?.message || "").toLowerCase();
          const isReview = type === "review" || msg === "ready for review";

          // Just-approved doc: the poll can briefly return the stale 'ready_for_review'
          // verdict before ingestion starts — keep it 'ingesting' and keep polling.
          if (pendingIngestRef.current.has(docKey)) {
            if (isReview) {
              return { key: docKey, status: "ingesting", data: res.data };
            }
            if (type === "completed" || type === "error" || msg === "ingested") {
              pendingIngestRef.current.delete(docKey); // ingestion finished
            }
          }

          let rawNewStatus = doc.status;
          if (type === "completed" || msg === "ingested") {
            rawNewStatus = "ingested";
          } else if (isReview) {
            rawNewStatus = "ready_for_review";
          } else if (type === "error") {
            rawNewStatus = "error";
          } else if (res.data?.message) {
            rawNewStatus = res.data.message;
          } else if (res.data?.status) {
            rawNewStatus = res.data.status;
          }
          return { key: docKey, status: rawNewStatus, data: res.data };
        } catch (err) {
          return null;
        }
      });

      const statuses = (await Promise.all(statusPromises)).filter(Boolean);

      if (statuses.length > 0) {
        let hasChanges = false;
        let requiresFullFetch = false;

        statuses.forEach((s) => {
          const existingDoc = localFilesRef.current.find((c) => (c.key || c.pdf_key || c.id) === s.key);
          if (existingDoc && existingDoc.status !== s.status) {
            hasChanges = true;
            const newSt = (s.status || "").toLowerCase();
            if (newSt.includes("ready") || newSt === "ingested" || newSt === "completed" || newSt === "error" || newSt === "failed") {
              requiresFullFetch = true;
            }
          }
        });

        if (hasChanges) {
          setActivePollingDocs((prev) => {
            const next = new Set(prev);
            statuses.forEach((s) => {
              const newSt = (s.status || "").toLowerCase();
              if (newSt.includes("ready") || newSt === "ingested" || newSt === "completed" || newSt === "error" || newSt === "failed") {
                next.delete(s.key);
              }
            });
            return next;
          });

          setLocalFiles((prev) =>
            prev.map((c) => {
              const updated = statuses.find((s) => s.key === (c.key || c.pdf_key || c.id));
              return updated && updated.status !== c.status 
                ? { ...c, ...updated.data, status: updated.status, isOptimistic: true } 
                : c;
            })
          );

          if (requiresFullFetch) {
            if (fetchFiles) fetchFiles();

            try {
              const orgId = Cookies.get("selected_org_id");
              const filesUrl = orgId
                ? `/api/v1/rfp/folders/${folderId}/files/?organization_id=${encodeURIComponent(orgId)}`
                : `/api/v1/rfp/folders/${folderId}/files/`;
              const res = await api.get(filesUrl);
              const fetchedList = res.data.results || res.data.documents || res.data || [];
              if (Array.isArray(fetchedList)) {
                setLocalFiles((prev) => {
                  const backendKeys = new Set(fetchedList.map((f) => f.key || f.pdf_key || f.id));
                  const tempDocs = prev.filter((p) => p.isTemp);
                  const optimisticDocs = prev.filter((p) => !p.isTemp && p.isOptimistic && !backendKeys.has(p.key || p.pdf_key || p.id));
                  // While uploads/ingests are in flight a transient/partial /files/ response can
                  // omit real rows — keep them instead of dropping them off the table.
                  const carriedDocs = activePollingDocsRef.current.size > 0
                    ? prev.filter((p) => !p.isTemp && !p.isOptimistic && !backendKeys.has(p.key || p.pdf_key || p.id))
                    : [];

                  const mergedFetched = fetchedList.map((f) => {
                    const fKey = f.key || f.pdf_key || f.id;
                    const existing = prev.find(p => (p.key || p.pdf_key || p.id) === fKey);
                    const eStatus = (existing?.status || "").toLowerCase();
                    const bStatus = (f.status || "").toLowerCase();
                    // A just-approved doc may still report the stale 'ready_for_review' verdict
                    // while ingestion spins up — keep showing it as ingesting until it progresses.
                    if (existing && pendingIngestRef.current.has(fKey) && bStatus === "ready_for_review") {
                      return { ...f, status: existing.status };
                    }
                    // A lagging /files/ verdict can still say 'ready_for_review' after the doc
                    // finished ingesting — don't pull it backwards.
                    if (existing && bStatus === "ready_for_review" && (eStatus === "ingested" || eStatus === "completed" || eStatus === "ready")) {
                      return { ...f, status: existing.status };
                    }
                    if (existing && existing.isOptimistic) {
                       if (bStatus.includes("ready") || bStatus === "ingested" || bStatus === "completed" || bStatus === "error" || bStatus === "failed") {
                          return f;
                       }
                       return { ...f, ...existing };
                    }
                    return f;
                  });
                  return [...tempDocs, ...optimisticDocs, ...carriedDocs, ...mergedFetched];
                });
              }
            } catch (err) {
              console.error("Silent table refresh failed", err);
            }
          }
        }
      }
    } catch (error) {
      console.error("Polling error:", error);
    } finally {
      isPollingRef.current = false;
    }
  }, [folderId, fetchFiles]);

  useEffect(() => {
    let intervalId;
    if (activePollingDocs.size > 0) {
      intervalId = setInterval(() => {
        pollDocumentStatuses();
      }, 2000);
    }
    return () => clearInterval(intervalId);
  }, [activePollingDocs.size, pollDocumentStatuses]);

  const hasProcessingMetaSummary = useMemo(
    () =>
      localFiles.some(
        (doc) => (doc.meta_summary || "").toLowerCase() === "processing"
      ),
    [localFiles]
  );

  useEffect(() => {
    if (!hasProcessingMetaSummary) return;

    const orgId = Cookies.get("selected_org_id");
    const filesUrl = orgId
      ? `/api/v1/rfp/folders/${folderId}/files/?organization_id=${encodeURIComponent(orgId)}`
      : `/api/v1/rfp/folders/${folderId}/files/`;

    const pollMetaSummary = async () => {
      try {
        const res = await api.get(filesUrl);
        const fetchedList = res.data.documents || res.data.results || [];
        if (!Array.isArray(fetchedList)) return;

        setLocalFiles((prev) =>
          prev.map((c) => {
            const cKey = c.key || c.pdf_key || c.id;
            const updated = fetchedList.find(
              (f) => (f.key || f.pdf_key || f.id) === cKey
            );
            if (!updated) return c;
            return {
              ...c,
              meta_summary: updated.meta_summary,
              summary: updated.summary,
            };
          })
        );
      } catch (err) {
        console.error("meta_summary polling error", err);
      }
    };

    const intervalId = setInterval(pollMetaSummary, 2000);
    return () => clearInterval(intervalId);
  }, [hasProcessingMetaSummary, folderId]);

  const sortedFiles = useMemo(() => {
    return [...localFiles].sort((a, b) => {
      if (a.isTemp) return -1;
      if (b.isTemp) return 1;

      const parseDate = (dStr) => {
        if (!dStr) return 0;
        const cleanStr = dStr.replace(/\//g, "-");
        const pts = cleanStr.split("-");
        if (pts.length === 3) {
          return new Date(`${pts[2]}-${pts[1]}-${pts[0]}`).getTime();
        }
        return new Date(dStr).getTime() || 0;
      };
      
      return parseDate(b.uploaded_at) - parseDate(a.uploaded_at);
    });
  }, [localFiles]);

  const filteredFiles = useMemo(() => {
    return sortedFiles.filter((file) => {
      const matchesSearch = (file.name || "").toLowerCase().includes(searchQuery.toLowerCase());
      
      let matchesFileType = true;
      if (activeFilters.fileTypes && activeFilters.fileTypes.length > 0) {
        const typeLower = (file.file_type || "pdf").toLowerCase();
        matchesFileType = activeFilters.fileTypes.some(ft => typeLower === ft.toLowerCase());
      }
      
      let matchesStatus = true;
      if (activeFilters.statuses && activeFilters.statuses.length > 0) {
        const statusLower = (file.status || "uploading").toLowerCase();
        matchesStatus = activeFilters.statuses.some(st => {
           const s = st.toLowerCase();
           if (s === "ready for review") return statusLower === "ready_for_review" || statusLower === "ready for review";
           if (s === "ingested") return statusLower === "ingested" || statusLower === "completed" || statusLower === "ready";
           if (s === "rejected") return statusLower === "error" || statusLower === "failed" || statusLower === "rejected";
           return statusLower === s;
        });
      }
      
      let matchesTags = true;
      if (activeFilters.tags && activeFilters.tags.length > 0) {
        const fileTags = file.tags || [];
        const normalizedFileTags = fileTags.map(tag => {
          if (typeof tag === 'string') return tag.toLowerCase();
          if (tag && typeof tag === 'object' && tag.name) return tag.name.toLowerCase();
          return "";
        });

        matchesTags = activeFilters.tags.some(filterTag => 
          normalizedFileTags.includes(filterTag.toLowerCase())
        );
      }
      
      return matchesSearch && matchesFileType && matchesStatus && matchesTags;
    });
  }, [sortedFiles, searchQuery, activeFilters]);

  const totalPages = Math.ceil(filteredFiles.length / rowsPerPage);
  const currentFiles = useMemo(() => {
    const indexOfLastFile = currentPage * rowsPerPage;
    const indexOfFirstFile = indexOfLastFile - rowsPerPage;
    return filteredFiles.slice(indexOfFirstFile, indexOfLastFile);
  }, [filteredFiles, currentPage, rowsPerPage]);

  const isAllChecked = selectedFiles.length === filteredFiles.length && filteredFiles.length > 0;

  const handleSelectAll = (e) => {
    if (isAllChecked) setSelectedFiles([]);
    else setSelectedFiles(
      filteredFiles.filter((f) => !f.isTemp && !f.isTenderImport).map((f) => f.id)
    );
  };

  const handleCheckboxChange = (id) => {
    setSelectedFiles((prev) =>
      prev.includes(id) ? prev.filter((fId) => fId !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = useCallback(() => {
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error("Please select at least one item to delete.");
      return;
    }
    setConfirmDeleteVisible(true);
  }, [selectedFiles]);

  const handleCancelBulk = () => {
    setSelectedFiles([]);
  };

  const confirmDelete = async () => {
    setDeleteInProgress(true);
    try {
      const payload = { doc_ids: selectedFiles, folder_ids: [] };
      const res = await api.delete("/api/v1/rfp/documents/delete/", { data: payload });
      if (res.status === 200) {
        setDeleteInProgress(false);
        setDeleteSuccess(true);
        setTimeout(() => {
          setConfirmDeleteVisible(false);
          setDeleteSuccess(false);
          setSelectedFiles([]);
          if (fetchFiles) fetchFiles();
        }, 2000);
      }
    } catch (error) {
      setDeleteInProgress(false);
      setDeleteSuccess(false);
      toast.error("Failed to delete item(s).");
    }
  };

  const handleApproveAndIngest = async (item) => {
    const docKey = item.key || item.pdf_key || item.id;
    try {
      const res = await api.post(`/api/v1/rfp/folders/${folderId}/documents/${docKey}/ingest/`, {
        action: "approve",
      });
      const ingestStatus = res.data?.status || "ingesting";

      pendingIngestRef.current.add(docKey);
      setActivePollingDocs((prev) => new Set([...prev, docKey]));
      setLocalFiles((prev) =>
        prev.map((doc) =>
          (doc.key || doc.pdf_key || doc.id) === docKey
            ? { ...doc, status: ingestStatus, isOptimistic: true }
            : doc
        )
      );

      setTimeout(() => pollDocumentStatuses(), 1000);
    } catch (error) {
      toast.error("Failed to start ingestion.");
      throw error;
    }
  };

  const handleRejectAndDelete = async (item) => {
    const docKey = item.key || item.pdf_key || item.id;
    try {
      await api.delete(`/api/v1/rfp/folders/${folderId}/documents/${docKey}/reject/`);
      setLocalFiles((prev) => prev.filter((doc) => (doc.key || doc.pdf_key || doc.id) !== docKey));
      if (fetchFiles) fetchFiles();
     
    } catch (error) {
      toast.error("Failed to reject document.");
      throw error;
    }
  };

  const removeFileExtension = (filename) => {
    if (!filename) return "N/A";
    if (filename.includes(".")) {
      const lastDotIndex = filename.lastIndexOf(".");
      if (lastDotIndex > 0) return filename.substring(0, lastDotIndex);
    }
    return filename;
  };

  const getFileIcon = (item) => {
    switch (item.file_type) {
      case "pdf": return "/pdff.svg";
      case "xlsx": case "xls": return "/Xls.svg";
      case "docx": case "doc": return "/docX.svg";
      case "csv": return "/CSV.svg";
      case "txt": return "/TXT.svg";
      default: return "/pdf.svg";
    }
  };

  const renderStatus = (item) => {
    const rawStatus = item.status || "uploading";
    const statusLower = rawStatus.toLowerCase();
    const displayLabel = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).replace(/_/g, " ");

    let config = {
      label: displayLabel, color: "text-[#6A6A75]", bg: "bg-[#F2F2F2]", border: "border-[#52525B4D]", icon: <SpinnerIcon />, isSpinning: true,
    };

    if (statusLower === "ready" || statusLower === "ingested" || statusLower === "completed") {
      config = { label: displayLabel, color: "text-[#0C7A2A]", bg: "bg-[#BCE5C766]", border: "border-[#00942866]", icon: <CheckIcon />, isSpinning: false };
    } else if (statusLower === "ready_for_review" || statusLower === "ready for review") {
      config = { label: "Ready for review", color: "text-[#B89A25]", bg: "bg-[#FFFAE5]", border: "border-[#D1A70066]", icon: <ClockIcon />, isSpinning: false };
    } else if (statusLower === "error" || statusLower === "failed") {
      config = { label: displayLabel, color: "text-red-700", bg: "bg-red-50", border: "border-red-200", icon: <ErrorIcon />, isSpinning: false };
    }

    return (
      <span title={config.label} className={`inline-flex max-w-full font-outfit items-center gap-1.5 px-2.5 py-1 rounded-full border ${config.border} ${config.bg} ${config.color} text-[12px] font-medium`}>
        <span className={`flex-shrink-0 flex items-center justify-center w-3.5 h-3.5 ${config.isSpinning ? "animate-spin" : ""}`}>{config.icon}</span>
        <span className="truncate">{config.label}</span>
      </span>
    );
  };

  const toggleSummary = (id, e) => {
    e.stopPropagation();
    const anchor = e.currentTarget;
    setActiveSummaryId((prevId) => {
      const opening = prevId !== id;
      setSummaryAnchorEl(opening ? anchor : null);
      return opening ? id : null;
    });
  };

  const toggleActionMenu = (id, event) => {
    event.stopPropagation();
    if (isActionMenuOpen === id) {
      setIsActionMenuOpen(null);
      setActionMenuAnchorEl(null);
    } else {
      setIsActionMenuOpen(id);
      setActionMenuAnchorEl(event.currentTarget);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (summaryModalRef.current && !summaryModalRef.current.contains(event.target)) {
        setActiveSummaryId(null);
        setSummaryAnchorEl(null);
      }
      if (confirmDeleteVisible && event.target.closest(".popup-overlay") === null && !event.target.closest(".popup-box-delete")) setConfirmDeleteVisible(false);
      
      // FIX: Ensure we only close the menu if the user actually clicked OUTSIDE the portal menu box.
      // Previously, clicking 'View & Ingest' fired the native mousedown and unmounted the menu before the click finished.
      if (isActionMenuOpen && !event.target.closest('[role="menu"]')) {
         setIsActionMenuOpen(null);
         setActionMenuAnchorEl(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeSummaryId, isActionMenuOpen, confirmDeleteVisible]);

  const handleResize = (colName, e) => {
    e.preventDefault();
    e.stopPropagation();
    const thElement = e.target.closest("th");
    const startWidth = thElement ? thElement.getBoundingClientRect().width : 150;
    const startX = e.clientX;

    const onMouseMove = (moveEvent) => {
      const newWidth = Math.max(80, startWidth + (moveEvent.clientX - startX));
      setColumnWidths((prev) => ({ ...prev, [colName]: newWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "default";
      setColumnWidths((latestWidths) => {
        localStorage.setItem("projectFiles_columnWidths", JSON.stringify(latestWidths));
        return latestWidths;
      });
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
  };

  const getColStyle = (colName) => {
    return columnWidths[colName]
      ? { width: columnWidths[colName], minWidth: columnWidths[colName], maxWidth: columnWidths[colName] }
      : {};
  };

  const showToast = (type, message) => {
    if (type === "success") toast.success(message);
    else if (type === "error") toast.error(message);
    else toast(message);
  };

  const handleRenameClick = (item) => {
    setIsActionMenuOpen(null);
    setActionMenuAnchorEl(null);
    setCurrentFile(item);
    setRenameInput(removeFileExtension(item.name) || "");
    setIsRenameModalOpen(true);
  };

  const handleRenameSubmit = async (newName) => {
    if (!currentFile) {
      showToast("error", "No file selected for renaming.");
      return;
    }
    if (!newName || newName.trim() === "") {
      showToast("error", "File name cannot be empty.");
      return;
    }

    setIsRenamingDoc(true);
    try {
      const response = await api.put(
        `/api/v1/readpdf/rename-doc/${currentFile.id}/`,
        { new_name: newName.trim() },
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.status === 200) {
        if (fetchFiles) await fetchFiles();
        setIsRenameModalOpen(false);
        setCurrentFile(null);
        setRenameInput("");
        showToast("success", "Document renamed successfully!");
      }
    } catch (error) {
      console.error("Error renaming document:", error);
      showToast("error", "Failed to rename document. Please try again.");
    } finally {
      setIsRenamingDoc(false);
    }
  };

  const handleDownload = async (file) => {
    setIsActionMenuOpen(null);
    setActionMenuAnchorEl(null);
    if (!file.url) {
      showToast("error", "Download URL not available for this file.");
      return;
    }
    const fileName = file.name || `document.${file.file_type || "file"}`;
    try {
      const response = await fetch(file.url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      showToast("success", `Downloading ${fileName}...`);
    } catch (error) {
      console.error("Download error:", error);
      // Could not fetch the file for a renamed download (e.g. CORS) —
      // fall back to opening it in a separate tab.
      const opened = window.open(file.url, "_blank", "noopener,noreferrer");
      if (!opened) showToast("error", "Failed to download file. Please try again.");
    }
  };

  const handleViewFile = (file) => {
    setIsActionMenuOpen(null);
    setActionMenuAnchorEl(null);
    setFileToView(file);
    setIsViewFileModalOpen(true);
  };

  const handleDeleteClick = (item) => {
    setIsActionMenuOpen(null);
    setActionMenuAnchorEl(null);
    setSelectedFiles([item.id]);
    setConfirmDeleteVisible(true);
  };

  const openTagModalFor = useCallback((docIdOrIds) => {
    const ids = Array.isArray(docIdOrIds) ? docIdOrIds : [docIdOrIds];
    setTagModalDocIds(ids);
    setIsTagModalOpen(true);
  }, []);

  const activeColumns = [
    { name: "name", visible: true, order: 0 },
    { name: "status", visible: true, order: 1 },
    { name: "tags", visible: true, order: 2 },
    { name: "uploaded_by", visible: true, order: 3 },
    { name: "uploaded_at", visible: true, order: 4 },
  ];

  const columnDefs = {
    name: {
      header: (
        <th key="name" style={getColStyle("name")} className={`${!columnWidths.name ? "w-[28%]" : ""} relative px-4 text-[#00000080] font-outfit text-[13px] font-semibold border-b border-r border-[#0000001A]`}>
          Document
          <div onMouseDown={(e) => handleResize("name", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#A259FF] transition-colors z-10" />
        </th>
      ),
      cell: (item) => (
        <td key={`name-${item.id}`} style={getColStyle("name")} className={`${!columnWidths.name ? "w-[28%]" : ""} px-4 py-2 border-b border-r border-[#0000001A] relative`}>
          <div className="flex items-center gap-3 min-w-0">
            <img src={getFileIcon(item)} className="w-5 h-5 flex-shrink-0" alt="Icon" />
            <span className="text-[#52525B] font-outfit text-[14px] font-medium truncate">
              {removeFileExtension(item.name) || "N/A"}
            </span>

            {(item.meta_summary || "").toLowerCase() === "processing" && (
              <span className="flex items-center justify-center flex-shrink-0 w-[22px] h-[22px] rounded-full bg-[#E5F0FF] text-[#408DFB]" title="Generating summary">
                <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
              </span>
            )}

            {(item.meta_summary || "").toLowerCase() === "done" && item.summary && (
              <div className="relative">
                <button onClick={(e) => toggleSummary(item.id, e)} className="flex items-center justify-center flex-shrink-0 w-[22px] h-[22px] rounded-full bg-[#E5F0FF] text-[#408DFB] hover:bg-[#D0E3FF] transition-colors" title="View Summary">
                  <DocumentIcon />
                </button>

                {activeSummaryId === item.id && (
                  <SummaryPopover
                    anchorEl={summaryAnchorEl}
                    summary={item.summary}
                    popoverRef={summaryModalRef}
                  />
                )}
              </div>
            )}
          </div>
        </td>
      ),
    },
    status: {
      header: (
        <th key="status" style={getColStyle("status")} className={`${!columnWidths.status ? "w-[18%]" : ""} relative px-3 text-[#00000080] font-outfit text-[13px] font-semibold border-b border-r border-[#0000001A]`}>
          Status
          <div onMouseDown={(e) => handleResize("status", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#A259FF] transition-colors z-10" />
        </th>
      ),
      cell: (item) => (
        <td key={`status-${item.id}`} style={getColStyle("status")} className={`${!columnWidths.status ? "w-[18%]" : ""} px-3 py-2 border-b border-r border-[#0000001A]`}>
          {renderStatus(item)}
        </td>
      ),
    },
    tags: {
      header: (
        <th key="tags" style={getColStyle("tags")} className={`${!columnWidths.tags ? "w-[18%]" : ""} relative px-4 text-[#00000080] font-outfit text-[13px] font-semibold border-b border-r border-[#0000001A]`}>
          Tags
          <div onMouseDown={(e) => handleResize("tags", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#A259FF] transition-colors z-10" />
        </th>
      ),
      cell: (item) => (
        <td key={`tags-${item.id}`} style={getColStyle("tags")} className={`${!columnWidths.tags ? "w-[18%]" : ""} px-1 py-2 border-b border-r border-[#0000001A] max-w-0`}>
          <DynamicTags
            tags={item.tags || []}
            itemId={item.id}
            darkenColor={(hex) => hex}
            openTagModalFor={openTagModalFor}
            isApplying={applyingTagDocIds.has(item.id)}
          />
        </td>
      ),
    },
    uploaded_by: {
      header: (
        <th key="uploaded_by" style={getColStyle("uploaded_by")} className={`${!columnWidths.uploaded_by ? "w-[15%]" : ""} relative px-4 text-[#00000080] font-outfit text-[13px] font-semibold border-b border-r border-[#0000001A]`}>
          Uploaded By
          <div onMouseDown={(e) => handleResize("uploaded_by", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#A259FF] transition-colors z-10" />
        </th>
      ),
      cell: (item) => {
        const uploaderName = item.uploaded_by || "N/A";
        return (
          <td key={`uploaded_by-${item.id}`} style={getColStyle("uploaded_by")} className={`${!columnWidths.uploaded_by ? "w-[15%]" : ""} px-4 py-2 border-b border-r border-[#0000001A] overflow-visible`}>
            <div className="relative group/avatar inline-block z-10 hover:z-[25]">
              <div className="w-9 h-9 rounded-full bg-[#C95EBE] flex items-center justify-center text-white font-outfit text-[13px] font-medium border-2 border-white transition-transform hover:scale-105 relative z-10 shadow-sm cursor-pointer">
                {getInitials(uploaderName)}
              </div>
              <div className="absolute top-[110%] left-1/2 -translate-x-1/2 hidden group-hover/avatar:block z-[25] min-w-[max-content]">
                <div className="bg-white rounded-[10px] shadow-[0_4px_20px_rgba(0,0,0,0.15)] border border-gray-100 py-2 px-3 text-center whitespace-nowrap">
                  <span className="font-outfit text-[#171717] font-medium text-[13px]">
                    {uploaderName} {item.permission && <span className="text-[#C95EBE] ml-1">({item.permission})</span>}
                  </span>
                </div>
              </div>
            </div>
          </td>
        );
      }
    },
    uploaded_at: {
      header: (
        <th key="uploaded_at" style={getColStyle("uploaded_at")} className={`${!columnWidths.uploaded_at ? "w-[15%]" : ""} relative px-4 text-[#00000080] font-outfit text-[13px] font-semibold cursor-pointer group border-b border-r border-[#0000001A]`}>
          <div className="flex items-center gap-1.5">
            Added On
            <SortIcon />
          </div>
          <div onMouseDown={(e) => handleResize("uploaded_at", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#A259FF] transition-colors z-10" />
        </th>
      ),
      cell: (item) => (
        <td key={`uploaded_at-${item.id}`} style={getColStyle("uploaded_at")} className={`${!columnWidths.uploaded_at ? "w-[15%]" : ""} px-4 py-2 text-[#52525B] font-outfit font-medium text-[13px] border-b border-r border-[#0000001A]`}>
          {item.uploaded_at}
        </td>
      ),
    },
  };

  const colSpanCount = 2 + activeColumns.length;

  const TabControls = (
    <div className="w-full pb-3 flex justify-between items-center gap-4">
      {selectedFiles.length > 0 ? (
        <div className="flex items-center justify-between bg-white gap-2 flex-shrink-0 w-full">
          <span className="text-[#A259FF] font-outfit text-gray-500 text-[13px] font-medium">
            {selectedFiles.length} Documents selected
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1 px-4 py-1.5 border border-red-500 text-red-500 hover:bg-red-50 rounded text-[13px] font-medium transition-colors"
            >
              Delete
            </button>
            <button
              onClick={handleCancelBulk}
              className=" transition-colors"
            >
              <img src="/cancel_icon.svg" className="w-4 h-4" alt="Cancel" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 ml-auto mb-3">
         <FilterDropdown
  projectId={folderId} // Pass the folderId here!
  initialFilters={activeFilters}
  onApply={(filters) => {
    setActiveFilters(filters);
    setCurrentPage(1);
  }}
  onClear={() => {
    setActiveFilters({ fileTypes: [], statuses: [], tags: [] });
    setCurrentPage(1);
  }}
  tagsRefreshTrigger={tagsRefreshTrigger}
/>
          <div className="relative w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
            <input
              type="text"
              placeholder="Search Documents..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1); 
              }}
              className="w-full pl-9 pr-4 py-1.5 border border-[#00000036] rounded text-[14px] font-outfit focus:outline-none focus:border-[#C95EBE] transition"
              style={{ height: '35px', fontSize: '14px', lineHeight: '20px' }}
            />
          </div>
        </div>
      )}
    </div>
  );

  const paginationUI = useMemo(() => {
    if (filteredFiles.length === 0) return null;
    return (
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredFiles.length}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    );
  }, [filteredFiles.length, currentPage, totalPages, rowsPerPage]);

  return (
    <div className="flex flex-col flex-1 w-full pl-2 pr-1 pt-3 pb-2 overflow-hidden bg-[#FAF9FB]">
      <input
        id="project-hidden-file-input"
        type="file"
        multiple
        className="hidden"
        accept=".pdf,.jpeg,.jpg,.png,.csv,.xlsx,.xls,.docx,.doc"
        onChange={handleFileChange}
      />

      {controlsPortalNode && createPortal(TabControls, controlsPortalNode)}

      {importStalled && importIsRunning && (
        <div className="mb-2 flex items-start gap-3 rounded-lg border border-[#FDE68A] bg-[#FEFCE8] px-4 py-3">
          <ClockIcon />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-[#854D0E]">
              This import hasn&apos;t made progress in a while
            </p>
            <p className="mt-0.5 text-[12px] text-[#854D0E]">
              We&apos;ve stopped checking for now. Retry to pick it up again, or reload
              the page to check on it.
            </p>
          </div>
          <button
            type="button"
            onClick={retryTenderImport}
            disabled={retryingImport}
            className="shrink-0 rounded-md border border-[#854D0E] px-3 py-1.5 text-[12px] font-semibold text-[#854D0E] hover:bg-[#FDE68A] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {retryingImport ? "Retrying…" : "Retry"}
          </button>
        </div>
      )}

      {failedImportFiles.length > 0 && (
        <div className="mb-2 flex items-start gap-3 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3">
          <ErrorIcon />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-[#B91C1C]">
              {failedImportFiles.length} document
              {failedImportFiles.length === 1 ? "" : "s"} couldn&apos;t be downloaded
              from the tender
            </p>
            <p className="mt-0.5 text-[12px] text-[#7F1D1D] break-words">
              {failedImportFiles[0].error || "The source did not answer."}
              {failedImportFiles.length > 1 && ` (and ${failedImportFiles.length - 1} more)`}
            </p>
          </div>
          <button
            type="button"
            onClick={retryTenderImport}
            disabled={retryingImport || importIsRunning}
            className="shrink-0 rounded-md bg-[#B91C1C] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#991B1B] disabled:opacity-50 disabled:cursor-not-allowed"
            title={importIsRunning ? "The import is still running" : "Download these again"}
          >
            {retryingImport ? "Retrying…" : "Retry"}
          </button>
        </div>
      )}

      {confirmDeleteVisible && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center popup-overlay">
          <div className="popup-box-delete bg-white rounded-lg p-6 w-[400px] shadow-xl">
            <h2 className="text-lg font-bold mb-2">
              Are you sure you want to delete the selected item(s)?
            </h2>
            <p className="text-gray-600 text-sm mb-6">
              Once deleted, the selected item(s) will be permanently removed.
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDeleteVisible(false)}
                disabled={deleteInProgress || deleteSuccess}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteInProgress || deleteSuccess}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteInProgress ? "Deleting..." : deleteSuccess ? "Deleted" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full h-full flex flex-col border border-[#0000001A] rounded-lg">
        <div className="relative overflow-auto custom-scrollbar flex-1 rounded-lg bg-white">
          <table className="w-full table-fixed text-left border-separate border-spacing-0">
            <thead className="h-[36px] sticky top-0 z-30 bg-[#F7F7F7]">
              <tr className="bg-[#F7F7F7]">
                <th className="w-[40px] min-w-[40px] pl-3 pr-2 border-b border-[#0000001A]">
                  <Checkbox
                    checked={isAllChecked}
                    onChange={handleSelectAll}
                    disabled={filteredFiles.length === 0}
                    showLabel={false}
                    size="md"
                  />
                </th>

                {activeColumns.map((col) => columnDefs[col.name]?.header)}

                <th className="w-[80px] min-w-[80px] px-2 text-[#00000080] font-outfit text-[13px] font-semibold text-center border-b border-[#0000001A]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {localFiles.length === 0 && (loading || files.length > 0) ? (
                Array.from({ length: 8 }).map((_, rowIdx) => (
                  <tr key={`skeleton-${rowIdx}`} className="animate-pulse">
                    {/* Checkbox column */}
                    <td className="w-[40px] min-w-[40px] pl-3 pr-2 py-[14px] border-b border-[#0000001A]">
                      <div className="h-4 w-4 rounded bg-[#E5E7EB]" />
                    </td>
                    {activeColumns.map((col, colIdx) => (
                      <td
                        key={`skeleton-${rowIdx}-${col.name}`}
                        className="px-2 py-[14px] border-b border-[#0000001A]"
                      >
                        <div
                          className="h-3.5 rounded bg-[#E5E7EB]"
                          style={{ width: `${[75, 50, 65, 55, 60][colIdx % 5]}%` }}
                        />
                      </td>
                    ))}
                    {/* Actions column */}
                    <td className="w-[80px] min-w-[80px] px-2 py-[14px] border-b border-[#0000001A]">
                      <div className="h-3.5 w-5 rounded bg-[#E5E7EB] mx-auto" />
                    </td>
                  </tr>
                ))
              ) : currentFiles.length > 0 ? (
                currentFiles.map((item) => (
                  <tr key={item.id} className="group hover:bg-[#F8F3FF] transition-colors cursor-default">
                    <td className="w-[40px] min-w-[40px] pl-3 pr-2 py-2 border-b border-[#0000001A]" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedFiles.includes(item.id)}
                        onChange={() => handleCheckboxChange(item.id)}
                        disabled={!item.id || item.isTemp || item.isTenderImport}
                        showLabel={false}
                        size="md"
                      />
                    </td>

                    {activeColumns.map((col) => columnDefs[col.name]?.cell(item))}

                    <td className="w-[80px] min-w-[80px] px-2 py-2 relative border-b border-[#0000001A]" onClick={(e) => e.stopPropagation()}>
                      {/* A row still downloading from SAM.gov has no document
                          behind it yet — no actions until it becomes real. */}
                      {!item.isTenderImport && (
                      <div
                        onClick={(e) => toggleActionMenu(item.id, e)}
                        className="cursor-pointer flex items-center w-8 h-8 rounded-md hover:bg-[#A259FF]/10 text-[#A259FF] justify-center transition-colors mx-auto"
                      >
                        <ThreeDotsIcon />
                      </div>
                      )}
                      
                      {isActionMenuOpen === item.id && createPortal(
                        <ActionMenu
                          item={item}
                          anchorEl={actionMenuAnchorEl}
                          closeMenu={() => {
                            setIsActionMenuOpen(null);
                            setActionMenuAnchorEl(null);
                          }}
                          onRename={handleRenameClick} 
                          onDownload={handleDownload}
                          onDelete={handleDeleteClick} 
                          onViewFile={handleViewFile}
                          onViewAndIngest={(selectedItem) => {
                            setIsActionMenuOpen(null);
                            setActionMenuAnchorEl(null);
                            setViewAndIngestItem(selectedItem);
                          }}
                        />,
                        document.body
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="hover:!bg-white hover:!shadow-none">
                  <td colSpan={colSpanCount} className="py-20 text-center border-b border-[#0000001A]">
                    <img src="/no-contracts.svg" alt="No Data" className="mx-auto mb-4 w-32" />
                    {/* "No files yet" reads as "you haven't uploaded anything".
                        When the import ran and found nothing to fetch, say that
                        instead, or the user goes looking for their own mistake. */}
                    {tenderImport?.status === "completed_no_documents" ? (
                      <>
                        <p className="text-[17px] font-outfit font-semibold text-[#1A1A1A] mb-1.5">
                          Nothing to download on this tender
                        </p>
                        {/* The job's own reason is specific (restricted, links
                            only, unsupported). Only fall back to the generic
                            line when it did not give one. */}
                        <p className="text-[14px] font-outfit text-[#71717A] max-w-[400px] mx-auto leading-relaxed">
                          {tenderImport.error ||
                            "The agency published no files we could bring in. Upload the solicitation documents to get started."}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-[17px] font-outfit font-semibold text-[#1A1A1A] mb-1.5">
                          No files yet
                        </p>
                        <p className="text-[14px] font-outfit text-[#71717A] max-w-[340px] mx-auto leading-relaxed">
                          Upload files to this project to get started. They'll show up here once added.
                        </p>
                      </>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {footerPortalNode && createPortal(paginationUI, footerPortalNode)}

      {isRenameModalOpen && (
        <RenameModal
          isOpen={isRenameModalOpen}
          onClose={() => {
            setIsRenameModalOpen(false);
            setCurrentFile(null);
          }}
          onRename={handleRenameSubmit}   
          fileName={renameInput}          
          loading={isRenamingDoc}         
        />
      )}

      {isViewFileModalOpen && (
        <ViewFileModal
          isOpen={isViewFileModalOpen}
          onClose={() => {
            setIsViewFileModalOpen(false);
            setFileToView(null);
          }}
          file={fileToView}
        />
      )}

      {isTagModalOpen && (
        <ProjectTagModal
          isOpen={isTagModalOpen}
          onClose={() => setIsTagModalOpen(false)}
          documentId={tagModalDocIds}
          tagTarget="document"
          displayedFiles={localFiles}
          onTagUpdate={async () => {
            // Show the "Applying…" pill on the affected rows while the table refetches.
            const docIds = Array.isArray(tagModalDocIds) ? tagModalDocIds : [tagModalDocIds];
            setApplyingTagDocIds(new Set(docIds));
            setTagsRefreshTrigger((prev) => prev + 1);
            try {
              if (fetchFiles) await fetchFiles(true);
            } finally {
              setApplyingTagDocIds(new Set());
            }
          }}
          onClearSelection={() => {
            setSelectedFiles([]);
          }}
        />
      )}

      {!!viewAndIngestItem && (
        <ViewAndIngestModal
          isOpen={!!viewAndIngestItem}
          onClose={() => setViewAndIngestItem(null)}
          item={viewAndIngestItem}
          onRejectAndDelete={handleRejectAndDelete}
          onApproveAndIngest={handleApproveAndIngest}
        />
      )}
    </div>
  );
};

export default ProjectFilesTab;